import React from 'react';
import { Dispatch } from 'redux';
import { useDispatch } from 'react-redux';
import i18n from '@/../i18n';
import { BasicBadge, palette, AiTextToolIcon } from '@dev/ee-components';
import { Typography } from '@material-ui/core';
import { BlockNames, EditorCustomFormat, EditorToolbarCustomFormatActionProps } from '@dev/template-editor';
import { AiTextGenerateStartCheck } from '@/store/aiText/actions';
import { AIIcon } from '@/services/icons/AIIButtonIcon';
import { HideToast, ShowToast } from '@/store/toast/actions';
import { AiTextItemType } from '@/store/aiText/types';
import { EditorType } from '@/services/dictionaries/EditorType';
import { AI_TEXT_CONTEXT } from '../../useAiTextModalParams/constants';
import { useTemplateEditorActions } from '../../useTemplateEditorActions';

const MAX_IMPROVE_TEXT_LENGTH = 2000;

const getDropdownListItems = (
    dispatch: Dispatch<any>,
    props: EditorToolbarCustomFormatActionProps,
    showAiTextToolModal: (props: EditorToolbarCustomFormatActionProps) => void
) => {
    const generateText = () => {
        showAiTextToolModal(props);
    };
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const improveText = () => {
        if (!props.selectedText?.length) {
            return;
        }
        if (props.selectedText?.length > MAX_IMPROVE_TEXT_LENGTH) {
            dispatch(
                ShowToast({
                    message: i18n.t('improvetext_selectionlimitmessage', {
                        count: props.selectedText?.length,
                        limit: MAX_IMPROVE_TEXT_LENGTH,
                    }),
                    severity: 'info',
                    duration: 6000,
                    width: 430,
                    onClose: HideToast,
                })
            );
            return;
        }
        dispatch(
            AiTextGenerateStartCheck({
                type: AiTextItemType.IMPROVE,
                timestamp: Date.now(),
                context: AI_TEXT_CONTEXT,
                request: props.selectedText,
            })
        );
        showAiTextToolModal(props);
    };
    return [
        {
            value: 0,
            title: (
                <>
                    <Typography variant="h5">{i18n.t('aitexttool')}</Typography>
                    <BasicBadge
                        style={{ marginLeft: 2, marginBottm: 0, border: 'none' }}
                        value={`${i18n.t('beta')}`}
                        backgroundColor={palette.teal[3]}
                        textColor={palette.teal[8]}
                    />
                </>
            ),
            icon: <AiTextToolIcon />,
            role: 'menuitemradio',
            padding: '22px 16px 13px',
            isHeader: true,
        },
        {
            value: 1,
            title: i18n.t('generatetext'),
            role: 'menuitemradio',
            padding: '13px 16px',
            onClick: generateText,
        },
        // disabled till backend is ready
        // {
        //     value: 2,
        //     title: i18n.t('improvetext'),
        //     role: 'menuitemradio',
        //     padding: '13px 16px',
        //     onClick: improveText,
        //     isDisabled: !props.selectedText?.length,
        // },
    ];
};

interface AiTextFormat {
    getAiTextFormat: () => EditorCustomFormat;
}

export const useAiTextFormat = (editorType?: EditorType): AiTextFormat => {
    const dispatch = useDispatch();
    const { showAiTextToolModal } = useTemplateEditorActions(editorType);

    const getAiTextFormat = (): EditorCustomFormat => ({
        name: 'editor/aitools',
        title: 'aitools',
        object: true,
        tagName: 'custom',
        className: null,
        attributes: { style: 'style' },
        edit: {
            dropdownList: {
                icon: <AIIcon />,
                label: i18n.t('aitexttool'),
                padding: '0 0 9px',
                popoverProps: {
                    position: 'bottom right',
                    offset: 0,
                },
                buttonColors: { backgroundColor: palette.indigo[6], onHoverColor: palette.indigo[7] },
                renderDropdownListItems: (props) => getDropdownListItems(dispatch, props, showAiTextToolModal),
            },
        },
        applyToBlocks: [BlockNames.PARAGRAPH, BlockNames.HEADING, BlockNames.LIST],
    });
    return {
        getAiTextFormat,
    };
};
